import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import {
  XMarkIcon,
  PhoneIcon,
  ChatBubbleLeftRightIcon,
  ClockIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import {
  qualifyingHint,
  getQualifyingStatusMeta,
  getQualifyingScoreTone,
  getVerdictMeta,
  getCallOutcomeMeta,
  scheduledCallLabel,
  nextCallReasonLabel,
  qualifyingCanStillChange,
  formatNextCall,
} from './qualifyingAgentStatus';
import QualifyingCallRecordingPlayer from './QualifyingCallRecordingPlayer';

/**
 * Replay modal for a Qualifying Agent session: the result, what happened (each call and when),
 * then every question with its answer, and the transcript.
 *
 * Laid out top to bottom in the order a recruiter reads it (23 Oct redesign): the result in
 * three numbers and a sentence, the activity, then the detail. One pill per question at most
 * (the rating); everything else is plain text, and the channel is said once per change rather
 * than on every line.
 *
 * The transcript (2 Nov) is the call recording transcribed after the call, word for word, with
 * each line seeking to its exact place in its call's recording. While a call is live the tab says
 * "Call in progress" and shows none; for a few minutes after, "Preparing the call transcript…"
 * (Refresh loads it — no timer). A call whose recording could not be transcribed shows its live
 * lines, each labelled "Live transcript".
 *
 * The fit score is shown as a number and a per-answer rating rather than the AI interview's
 * score ring. A ring implies a graded performance; these answers are facts about the candidate
 * (salary, notice period, willingness), and what a recruiter needs is which specific fact
 * counted against them — hence the one-line note beside each rating.
 */

const RATING_META = {
  strong: { label: 'Good fit', className: 'bg-green-50 text-green-700 border-green-200' },
  acceptable: { label: 'Workable', className: 'bg-gray-50 text-gray-600 border-gray-200' },
  concern: { label: 'Concern', className: 'bg-red-50 text-red-700 border-red-200' },
};

// How the answer review (server answerReview.js) read a question across the whole call.
const STATUS_NOTES = {
  partial: { label: 'Partly answered', className: 'text-amber-700' },
  declined: { label: 'Preferred not to say', className: 'text-gray-500' },
};

const TONE_TEXT = {
  gray: 'text-gray-500',
  blue: 'text-[#0062CC]',
  green: 'text-green-700',
  amber: 'text-amber-700',
  red: 'text-red-600',
};

const TONE_DOT = {
  gray: 'bg-gray-400',
  blue: 'bg-[#007BFF]',
  green: 'bg-green-500',
  amber: 'bg-amber-500',
  red: 'bg-red-500',
};

const Label = ({ children }) => (
  <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">{children}</p>
);

const Dot = ({ tone }) => <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${TONE_DOT[tone] || TONE_DOT.gray}`} />;

/**
 * One question's answer. With the answer review: its short summary of what the candidate said
 * over the whole call (a correction said later wins, conditions kept), the candidate's own words
 * it rests on — each a link to its line in the transcript — and "Not answered on the call" when
 * nothing they said answered it. Without one (older sessions, or the review failed): the words
 * the call saved, as before.
 */
function AnswerBlock({ q, rating, onQuote }) {
  const [showWords, setShowWords] = useState(false);
  if (q.status === 'not_answered') {
    return (
      <div>
        <p className="text-[13px] text-gray-400">Not answered on the call</p>
        {q.reviewed && q.answer && (
          <p className="mt-0.5 text-[11px] text-gray-400">
            Saved during the call (it did not answer this question): “{q.answer}”
          </p>
        )}
      </div>
    );
  }
  const shown = q.summary || q.answer;
  if (!shown) {
    return <p className="text-[13px] text-gray-400">Not answered</p>;
  }
  const words = (q.evidence || []).filter((e) => e.quote);
  const statusNote = STATUS_NOTES[q.status];
  // Small plain-text notes under the answer, one line, separated by dots.
  const notes = [
    q.channel === 'whatsapp' && { text: 'Answered on WhatsApp', className: 'text-green-700' },
    q.correctedFrom && { text: `Corrected later in the call (was: ${q.correctedFrom})`, className: 'text-amber-700' },
    statusNote && { text: statusNote.label, className: statusNote.className },
    q.missingParts?.length > 0 && { text: `Not given: ${q.missingParts.join(', ')}`, className: 'text-amber-700' },
    // The one fact behind this answer's rating. Shown, not only tooltipped: it is the whole
    // reason the score is worth trusting.
    rating?.note && { text: rating.note, className: 'text-gray-500' },
  ].filter(Boolean);
  return (
    <>
      <p className="text-[14px] leading-relaxed text-gray-800 whitespace-pre-line">{shown}</p>
      {(notes.length > 0 || words.length > 0) && (
        <p className="mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[12px]">
          {notes.map((n, i) => (
            <React.Fragment key={n.text}>
              {i > 0 && <span className="text-gray-300">·</span>}
              <span className={n.className}>{n.text}</span>
            </React.Fragment>
          ))}
          {words.length > 0 && (
            <>
              {notes.length > 0 && <span className="text-gray-300">·</span>}
              <button
                type="button"
                onClick={() => setShowWords((v) => !v)}
                className="font-medium text-[#007BFF] hover:underline"
              >
                {showWords ? 'Hide' : 'Show'} the candidate’s words
              </button>
            </>
          )}
        </p>
      )}
      {showWords && (
        <ul className="mt-1.5 space-y-1 border-l-2 border-gray-100 pl-3">
          {words.map((e) =>
            // `turn` is null when the line it came from is not in the transcript shown (the
            // server maps it, toReplayShape): the quote stays, without a link.
            e.turn == null ? (
              <li key={`none-${e.quote}`} className="text-[12px] text-gray-600">
                {e.role === 'agent' ? 'Confirmed: ' : ''}“{e.quote}”
              </li>
            ) : (
              <li key={`${e.turn}-${e.quote}`}>
                <button
                  type="button"
                  onClick={() => onQuote(e.turn)}
                  className="text-left text-[12px] text-gray-600 hover:text-[#0062CC]"
                  title="Show this line in the transcript"
                >
                  {e.role === 'agent' ? 'Confirmed: ' : ''}“{e.quote}”
                </button>
              </li>
            )
          )}
        </ul>
      )}
    </>
  );
}

export default function QualifyingAgentReplayModal({ screeningId, candidate, onClose }) {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [innerTab, setInnerTab] = useState('questions');

  const candidateId = candidate?.id || candidate?._id;

  // Read once when the modal opens. Nothing polls (22 Oct: a 5 s timer here re-read the whole
  // transcript for as long as the modal stayed open on a live or stuck session). While the
  // session can still change, the header's Refresh re-reads it on request.
  const [refreshing, setRefreshing] = useState(false);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!screeningId || !candidateId) return;
    let cancelled = false;
    setLoading(true);
    setError('');
    axios
      .get(`/api/screenings/${screeningId}/qualifying-agent-session/${candidateId}`)
      .then((res) => {
        if (!cancelled) setSession(res.data?.session || null);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err.response?.data?.message || 'Failed to load qualifying session');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [screeningId, candidateId]);

  // Keeps what is on screen while it re-reads; a failed refresh leaves the last good session.
  const refresh = useCallback(() => {
    if (!screeningId || !candidateId || refreshing) return;
    setRefreshing(true);
    axios
      .get(`/api/screenings/${screeningId}/qualifying-agent-session/${candidateId}`)
      .then((res) => {
        if (mountedRef.current && res.data?.session) setSession(res.data.session);
      })
      .catch(() => {})
      .finally(() => {
        if (mountedRef.current) setRefreshing(false);
      });
  }, [screeningId, candidateId, refreshing]);
  // The transcript is the call recording, transcribed after the call (2 Nov, server
  // postCallTranscript.js): none while a call is live, "preparing" for a few minutes after it.
  const transcriptStatus = session?.transcriptStatus || 'ready';
  const canRefresh =
    !loading && !!session && (qualifyingCanStillChange(session) || transcriptStatus === 'preparing');

  const perQuestion = session?.perQuestion || [];
  const transcript = useMemo(() => session?.transcript || [], [session]);
  const hasLiveFallback = transcript.some((turn) => turn.liveFallback);
  const candidateName = candidate?.name || session?.candidateName || 'Candidate';

  const channelCounts = useMemo(() => {
    let call = 0;
    let whatsapp = 0;
    for (const turn of transcript) {
      if (turn.channel === 'call') call += 1;
      else if (turn.channel === 'whatsapp') whatsapp += 1;
    }
    return { call, whatsapp };
  }, [transcript]);

  // Keyed by question index rather than zipped positionally: the breakdown deliberately skips
  // unscored questions (the intro), so the two lists are different lengths.
  const ratingByIndex = useMemo(
    () =>
      new Map(
        (session?.scoreBreakdown || [])
          .filter((row) => RATING_META[row.rating])
          .map((row) => [row.questionIndex, row])
      ),
    [session]
  );

  // One recording per call (22 Oct). A server from before that sends only `recording`, the latest.
  const recordings = useMemo(() => {
    if (Array.isArray(session?.recordings)) return session.recordings;
    const latest = session?.recording;
    return latest?.status && latest.status !== 'none' ? [{ id: '', attempt: null, ...latest }] : [];
  }, [session]);

  // Where each call line starts in its own call's recording. A line of the post-call transcript
  // carries its exact place (`recordingId`, `startSec`). A live-transcript line (older sessions,
  // or the labelled fallback) is stamped when it finished, so it starts where the previous line of
  // that recording ended (the first at 0). A turn gets a position only inside a recording's time —
  // a transcript holds every call.
  const audioRefs = useRef(new Map());
  const registerAudio = useCallback((id, element) => {
    if (element) audioRefs.current.set(id, element);
    else audioRefs.current.delete(id);
  }, []);
  const [readyIds, setReadyIds] = useState(() => new Set());
  const onPlayerReady = useCallback((id, ready) => {
    setReadyIds((prev) => {
      if (prev.has(id) === ready) return prev;
      const next = new Set(prev);
      if (ready) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);
  const seekByIndex = useMemo(() => {
    const positions = new Map();
    const windows = recordings
      .filter((r) => r.status === 'stored' && r.startedAt)
      .map((r) => {
        const start = new Date(r.startedAt).getTime();
        return { id: r.id, start, end: r.durationMs ? start + r.durationMs + 5000 : Infinity, previousEnd: start };
      });
    transcript.forEach((turn, idx) => {
      if (turn.recordingId && typeof turn.startSec === 'number') {
        positions.set(idx, { id: turn.recordingId, seconds: Math.max(0, turn.startSec - 0.3) });
        return;
      }
      if (turn.channel !== 'call' || !turn.timestamp) return;
      const end = new Date(turn.timestamp).getTime();
      const w = windows.find((x) => end >= x.start && end <= x.end);
      if (!w) return;
      positions.set(idx, { id: w.id, seconds: Math.max(0, (w.previousEnd - w.start) / 1000 - 0.5) });
      w.previousEnd = end;
    });
    return positions;
  }, [transcript, recordings]);
  const seekTo = ({ id, seconds }) => {
    const audio = audioRefs.current.get(id);
    if (!audio) return;
    audioRefs.current.forEach((other) => other !== audio && other.pause?.());
    audio.currentTime = seconds;
    audio.play().catch(() => {});
  };
  const formatSeek = (seconds) =>
    `${Math.floor(seconds / 60)}:${String(Math.floor(seconds % 60)).padStart(2, '0')}`;

  const answered = session?.answeredCount ?? 0;
  const total = session?.totalQuestions ?? 0;
  const hint = session ? qualifyingHint(session) : '';

  // A quote under an answer opens the transcript at its line.
  const turnRefs = useRef(new Map());
  const [highlightTurn, setHighlightTurn] = useState(null);
  const showTurn = (turn) => {
    setInnerTab('transcript');
    setHighlightTurn(turn);
  };
  useEffect(() => {
    if (innerTab !== 'transcript' || highlightTurn == null) return;
    turnRefs.current.get(highlightTurn)?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
  }, [innerTab, highlightTurn]);

  const statusMeta = session ? getQualifyingStatusMeta(session.status) : null;
  const verdict = session ? getVerdictMeta(session) : null;
  const score = session?.overallScore;
  const nextCall = session ? scheduledCallLabel(session) : '';
  const calls = session?.calls || [];

  const tabClass = (tab) =>
    `-mb-px border-b-2 px-1 pb-2.5 text-sm font-semibold transition-colors ${
      innerTab === tab ? 'border-[#007BFF] text-gray-900' : 'border-transparent text-gray-500 hover:text-gray-700'
    }`;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 p-4 sm:p-6">
      <div className="flex w-full max-w-3xl max-h-[90vh] flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
        <header className="flex items-start justify-between shrink-0 border-b border-gray-100 px-6 py-4">
          <div className="min-w-0">
            <h2 className="truncate text-lg font-semibold text-gray-900">{candidateName}</h2>
            <p className="mt-0.5 text-[13px] text-gray-500">
              Qualifying call{session?.mobile ? ` · ${session.mobile}` : ''}
            </p>
          </div>
          <div className="flex items-center gap-1">
            {canRefresh && (
              <button
                type="button"
                onClick={refresh}
                disabled={refreshing}
                className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-semibold text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors disabled:opacity-60"
                title="Load the latest transcript"
                data-testid="qualifying-replay-refresh"
              >
                <ArrowPathIcon className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
              aria-label="Close"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        </header>

        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <span className="inline-block h-6 w-6 animate-spin rounded-full border-2 border-[#007BFF]/60 border-t-transparent" />
            </div>
          ) : error ? (
            <div className="text-center py-20 text-sm text-red-600">{error}</div>
          ) : !session ? (
            <div className="text-center py-20 text-sm text-gray-500">No session data available</div>
          ) : (
            <div className="space-y-6">
              {/* The result: three values in one row, then what they mean in a sentence. */}
              <section>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Fit score</Label>
                    <p
                      className={`mt-1 text-2xl font-semibold tabular-nums ${
                        score != null ? TONE_TEXT[getQualifyingScoreTone(score)] : 'text-gray-300'
                      }`}
                      title={score != null ? undefined : 'Scored once every question has been answered.'}
                    >
                      {score != null ? `${score}%` : '—'}
                    </p>
                  </div>
                  <div>
                    <Label>{verdict ? 'Recommendation' : 'Status'}</Label>
                    <p
                      className={`mt-2 inline-flex items-center gap-2 text-[15px] font-semibold ${
                        verdict ? TONE_TEXT[verdict.tone] : 'text-gray-800'
                      }`}
                    >
                      <Dot tone={verdict ? verdict.tone : statusMeta.tone} />
                      {verdict ? verdict.label : statusMeta.label}
                    </p>
                  </div>
                  <div>
                    <Label>Answered</Label>
                    <p className="mt-1 text-2xl font-semibold tabular-nums text-gray-900">
                      <span>
                        {answered} of {total}
                      </span>
                    </p>
                  </div>
                </div>
                {(session.scoreSummary || hint) && (
                  // The summary is what the score means; the hint is what the state means.
                  <div className="mt-4 rounded-xl bg-gray-50 px-4 py-3 space-y-1">
                    {session.scoreSummary && (
                      <p className="text-[13px] leading-relaxed text-gray-800">{session.scoreSummary}</p>
                    )}
                    {hint && <p className="text-[13px] leading-relaxed text-gray-500">{hint}</p>}
                  </div>
                )}
              </section>

              {(calls.length > 0 || channelCounts.whatsapp > 0 || nextCall) && (
                // Every call and how it ended, so "Call scheduled" is never the whole story.
                <section>
                  <Label>Activity · times in IST</Label>
                  <ol className="mt-2 divide-y divide-gray-100 rounded-xl border border-gray-100" data-testid="qualifying-calls">
                    {calls.map((call, i) => {
                      const outcome = getCallOutcomeMeta(call.outcome);
                      const showCount =
                        call.pickedUp && call.outcome !== 'completed' && call.answeredCount > 0 && total > 0;
                      return (
                        <li key={call.attempt ?? i} className="flex items-center gap-3 px-3.5 py-2.5 text-[13px]">
                          <PhoneIcon className="h-4 w-4 shrink-0 text-gray-400" />
                          <span className="w-12 shrink-0 font-medium text-gray-800">Call {call.attempt ?? i + 1}</span>
                          <span className="w-32 shrink-0 tabular-nums text-gray-500">
                            {call.placedAt ? formatNextCall(call.placedAt) : ''}
                          </span>
                          <span className={`inline-flex items-center gap-1.5 font-medium ${TONE_TEXT[outcome.tone]}`}>
                            <Dot tone={outcome.tone} />
                            {outcome.label}
                          </span>
                          {showCount && (
                            <span className="ml-auto text-xs text-gray-400">
                              {call.answeredCount} of {total} answered
                            </span>
                          )}
                        </li>
                      );
                    })}
                    {channelCounts.whatsapp > 0 && (
                      <li className="flex items-center gap-3 px-3.5 py-2.5 text-[13px]">
                        <ChatBubbleLeftRightIcon className="h-4 w-4 shrink-0 text-gray-400" />
                        <span className="w-12 shrink-0 font-medium text-gray-800">WhatsApp</span>
                        <span className="text-gray-500">
                          {channelCounts.whatsapp} message{channelCounts.whatsapp === 1 ? '' : 's'}
                        </span>
                      </li>
                    )}
                    {nextCall && (
                      <li className="flex items-center gap-3 px-3.5 py-2.5 text-[13px]">
                        <ClockIcon className="h-4 w-4 shrink-0 text-gray-400" />
                        <span className="w-12 shrink-0 font-medium text-gray-800">Next</span>
                        <span className="tabular-nums text-gray-500">{nextCall}</span>
                        {nextCallReasonLabel(session) && (
                          <span className="ml-auto text-right text-[12px] text-gray-400" data-testid="qualifying-next-call-why">
                            {nextCallReasonLabel(session)}
                          </span>
                        )}
                      </li>
                    )}
                  </ol>
                </section>
              )}

              {session.unansweredQuestions?.length > 0 && (
                // The agent told the candidate the hiring team would confirm these: the list of
                // what to get back to them on (22 Oct).
                <section className="rounded-xl border border-amber-200 bg-amber-50/50 px-4 py-3" data-testid="qualifying-unanswered">
                  <p className="text-[13px] font-semibold text-amber-800">Questions to answer</p>
                  <p className="mt-0.5 text-xs text-amber-700">
                    The candidate asked these and the job details had no answer. They were told the hiring team
                    will get back to them.
                  </p>
                  <ul className="mt-2 list-disc space-y-1 pl-5 text-[13px] text-gray-800">
                    {session.unansweredQuestions.map((q, i) => (
                      <li key={i}>
                        <span>“{q.text}”</span>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              <QualifyingCallRecordingPlayer
                screeningId={screeningId}
                candidateId={candidateId}
                recordings={recordings}
                registerAudio={registerAudio}
                onReadyChange={onPlayerReady}
                hadCall={channelCounts.call > 0 || transcriptStatus === 'preparing'}
              />

              <section>
                <div className="flex gap-6 border-b border-gray-200">
                  <button type="button" onClick={() => setInnerTab('questions')} className={tabClass('questions')}>
                    Questions &amp; answers
                  </button>
                  <button type="button" onClick={() => setInnerTab('transcript')} className={tabClass('transcript')}>
                    Transcript
                    {transcript.length > 0 && (
                      <span className="ml-1.5 text-xs font-medium text-gray-400">{transcript.length}</span>
                    )}
                  </button>
                </div>

                {innerTab === 'questions' &&
                  (perQuestion.length > 0 ? (
                    <ol className="divide-y divide-gray-100">
                      {perQuestion.map((q) => {
                        const rating = ratingByIndex.get(q.index);
                        return (
                          <li key={q.index} className="flex gap-3 py-4">
                            <span className="w-6 shrink-0 pt-0.5 text-xs font-semibold tabular-nums text-gray-400">
                              Q{q.index + 1}
                            </span>
                            <div className="min-w-0 flex-1">
                              <div className="flex items-start gap-3">
                                <p className="flex-1 text-[14px] font-medium leading-snug text-gray-900">{q.prompt}</p>
                                {rating && (
                                  <span
                                    className={`shrink-0 inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold ${RATING_META[rating.rating].className}`}
                                    title={rating.note || undefined}
                                  >
                                    {RATING_META[rating.rating].label}
                                  </span>
                                )}
                              </div>
                              <div className="mt-1.5">
                                <AnswerBlock q={q} rating={rating} onQuote={showTurn} />
                              </div>
                            </div>
                          </li>
                        );
                      })}
                    </ol>
                  ) : (
                    <div className="text-center py-12 text-sm text-gray-500">No questions recorded</div>
                  ))}

                {innerTab === 'transcript' && transcriptStatus === 'in_call' && (
                  // The call comes first: nothing about its transcript is read while it is live.
                  <div className="text-center py-12 text-sm text-gray-500" data-testid="qualifying-transcript-in-call">
                    <p className="font-medium text-gray-700">Call in progress</p>
                    <p className="mt-1 text-[13px]">The transcript appears a few minutes after the call ends.</p>
                  </div>
                )}
                {innerTab === 'transcript' && transcriptStatus === 'preparing' && (
                  <div
                    className="mt-4 rounded-xl bg-gray-50 px-4 py-3 text-[13px] text-gray-600"
                    data-testid="qualifying-transcript-preparing"
                  >
                    <p className="font-medium text-gray-700">Preparing the call transcript…</p>
                    <p className="mt-0.5">
                      The recording is transcribed word for word after the call. It takes a few minutes — press Refresh
                      to load it.
                    </p>
                  </div>
                )}
                {innerTab === 'transcript' && hasLiveFallback && (
                  <div
                    className="mt-4 rounded-xl border border-amber-200 bg-amber-50/50 px-4 py-3 text-[13px] text-amber-800"
                    data-testid="qualifying-transcript-live-fallback"
                  >
                    Lines marked “Live transcript” come from the call as it happened: the recording could not be
                    transcribed, so they may miss words. The recording itself is complete.
                  </div>
                )}
                {innerTab === 'transcript' &&
                  transcriptStatus !== 'in_call' &&
                  (transcript.length > 0 ? (
                    <div className="space-y-3 pt-4">
                      {transcript.map((turn, idx) => {
                        // The schema stores 'agent', but toReplayShape (qualifying-agent.service.js)
                        // renames it to 'ai' at the read boundary. Each side was once "fixed" to
                        // match the other, which left every agent line rendering as the candidate.
                        // Accept both so neither side can reintroduce that on its own.
                        const isAgent = turn.role === 'ai' || turn.role === 'agent';
                        const isWhatsApp = turn.channel === 'whatsapp';
                        // The channel is said once where it changes, not on every line.
                        const channelChanged = turn.channel && turn.channel !== transcript[idx - 1]?.channel;
                        const seek = seekByIndex.get(idx);
                        return (
                          <React.Fragment key={idx}>
                            {channelChanged && (
                              <div className="flex items-center gap-2 pt-1 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
                                {isWhatsApp ? (
                                  <ChatBubbleLeftRightIcon className="h-3.5 w-3.5" />
                                ) : (
                                  <PhoneIcon className="h-3.5 w-3.5" />
                                )}
                                {isWhatsApp ? 'WhatsApp' : 'Phone call'}
                                <span className="h-px flex-1 bg-gray-100" />
                              </div>
                            )}
                            <div
                              ref={(el) => (el ? turnRefs.current.set(idx, el) : turnRefs.current.delete(idx))}
                              className={`flex ${isAgent ? 'justify-start' : 'justify-end'}`}
                            >
                              <div
                                className={`max-w-[85%] rounded-xl ${
                                  highlightTurn === idx ? 'ring-2 ring-[#007BFF]/40 ring-offset-2' : ''
                                }`}
                              >
                                <div
                                  className={`mb-1 flex items-center gap-2 text-[11px] text-gray-500 ${
                                    isAgent ? '' : 'justify-end'
                                  }`}
                                >
                                  <span className="font-semibold text-gray-600">{isAgent ? 'Agent' : candidateName}</span>
                                  {isAgent && turn.cutOff && (
                                    <span
                                      className="text-amber-700"
                                      title="The candidate spoke over this line, so they may have heard only its start"
                                    >
                                      · Cut off
                                    </span>
                                  )}
                                  {turn.liveFallback && (
                                    <span
                                      className="text-amber-700"
                                      title="From the live call transcript — the recording could not be transcribed, so words may be missing"
                                    >
                                      · Live transcript
                                    </span>
                                  )}
                                  {seek && readyIds.has(seek.id) && (
                                    <button
                                      type="button"
                                      onClick={() => seekTo(seek)}
                                      className="font-semibold text-[#007BFF] hover:underline"
                                      title="Play this call's recording from here"
                                    >
                                      ▶ {formatSeek(seek.seconds)}
                                    </button>
                                  )}
                                </div>
                                <p
                                  className={`whitespace-pre-line rounded-2xl px-3.5 py-2 text-[14px] leading-relaxed ${
                                    isAgent
                                      ? 'rounded-tl-sm bg-gray-100 text-gray-800'
                                      : isWhatsApp
                                        ? 'rounded-tr-sm bg-green-50 text-gray-900'
                                        : 'rounded-tr-sm bg-[#007BFF]/10 text-gray-900'
                                  }`}
                                >
                                  {turn.text}
                                </p>
                              </div>
                            </div>
                          </React.Fragment>
                        );
                      })}
                    </div>
                  ) : transcriptStatus === 'preparing' ? null : (
                    <div className="text-center py-12 text-sm text-gray-500">No transcript available</div>
                  ))}
              </section>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
